import type { FollowUpConfig, FollowUpConfigDraft } from 'services/types/followUpConfig';

import baseApi from './baseApi';

const followUpConfigApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getFollowUps: build.query({
      query: ({ businessUnitId, isArchived }: { businessUnitId: number; isArchived?: boolean }) => {
        const params = new URLSearchParams();
        if (isArchived !== undefined) {
          params.append('isArchived', String(isArchived));
        }
        const queryString = params.toString();
        return {
          url: `/followUpConfig/businessUnit/${businessUnitId}${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      transformResponse: (response: FollowUpConfig[]) => response,
      providesTags: (result, _error, { businessUnitId, isArchived }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'FollowUpConfig' as const, id })),
              { type: 'FollowUpConfig', id: `LIST-${businessUnitId}-${isArchived ?? 'all'}` },
            ]
          : [{ type: 'FollowUpConfig', id: `LIST-${businessUnitId}-${isArchived ?? 'all'}` }],
    }),
    getFollowUpCampaigns: build.query({
      query: ({
        businessUnitId,
        isActive,
        hasSurvey,
      }: {
        businessUnitId: number;
        isActive?: boolean;
        hasSurvey?: boolean;
      }) => {
        const params = new URLSearchParams();
        if (isActive !== undefined) params.append('isActive', String(isActive));
        if (hasSurvey !== undefined) params.append('hasSurvey', String(hasSurvey));
        const queryString = params.toString();
        return {
          url: `/followUpConfig/follow-up-campaigns/${businessUnitId}${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      transformResponse: (response: FollowUpConfig[]) => response,
      providesTags: (result, _error, { businessUnitId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'FollowUpConfig' as const, id })),
              { type: 'FollowUpConfig', id: `CAMPAIGNS-${businessUnitId}` },
            ]
          : [{ type: 'FollowUpConfig', id: `CAMPAIGNS-${businessUnitId}` }],
    }),
    editFollowUp: build.mutation({
      query: (followUpConfig: FollowUpConfigDraft & { id: number }) => ({
        url: `followUpConfig/${followUpConfig.id}`,
        method: 'PATCH',
        body: followUpConfig,
      }),
      transformResponse: (response: FollowUpConfig) => response,
      invalidatesTags: (_result, _error, followUpConfig) => [
        { type: 'FollowUpConfig', id: followUpConfig.id },
        { type: 'FollowUpConfig', id: `LIST-${followUpConfig.businessUnitId}-false` },
        { type: 'FollowUpConfig', id: `LIST-${followUpConfig.businessUnitId}-true` },
        { type: 'FollowUpConfig', id: `LIST-${followUpConfig.businessUnitId}-all` },
        { type: 'FollowUpConfig', id: `CAMPAIGNS-${followUpConfig.businessUnitId}` },
      ],
    }),
    createFollowUp: build.mutation({
      query: (followUpConfig: FollowUpConfigDraft) => ({
        url: `followUpConfig`,
        method: 'POST',
        body: followUpConfig,
      }),
      transformResponse: (response: FollowUpConfig) => response,
      invalidatesTags: (_result, _error, followUpConfig) => [
        { type: 'FollowUpConfig', id: `LIST-${followUpConfig.businessUnitId}` },
        { type: 'FollowUpConfig', id: `CAMPAIGNS-${followUpConfig.businessUnitId}` },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetFollowUpsQuery,
  useEditFollowUpMutation,
  useGetFollowUpCampaignsQuery,
  useCreateFollowUpMutation,
} = followUpConfigApi;
